import { useState, useEffect } from "react";
import { api, formatCurrency, formatPercent, formatDate, monthNames } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLockedAccounts, LockedBadge, UnlockButton } from "@/components/security/LockedAccounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Plus, Trash2, Loader2, Shield, ShieldCheck, ShieldOff, Users, Target, Settings, 
  DollarSign, Key, ArrowRightLeft
} from "lucide-react";

export const AdminPage = () => {
  const { isAdmin } = useAuth();
  const { locked, refresh: refreshLocked } = useLockedAccounts(isAdmin);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all"); // all | admin | user
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [resetDialog, setResetDialog] = useState(null); // {id, email} or null
  const [newPassword, setNewPassword] = useState("");
  
  const [userData, setUserData] = useState({ email: "", password: "", name: "", role: "user" });
  const [targetData, setTargetData] = useState({ 
    year: new Date().getFullYear(), 
    month: new Date().getMonth() + 1, 
    target_ca: "" 
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2015 + 2 }, (_, i) => currentYear + 1 - i);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, targetsRes] = await Promise.all([
        api.getUsers(),
        api.getMonthlyTargets()
      ]);
      setUsers(usersRes.data);
      setTargets(targetsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.createUser(userData);
      toast.success("Utilisateur créé");
      setUserDialogOpen(false);
      setUserData({ email: "", password: "", name: "", role: "user" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try {
      await api.deleteUser(id);
      toast.success("Utilisateur supprimé");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialog) return;
    if (newPassword.length < 6) { toast.error("Mot de passe trop court (min. 6 caractères)"); return; }
    try {
      await api.resetUserPassword(resetDialog.id, { new_password: newPassword });
      toast.success(`Mot de passe réinitialisé pour ${resetDialog.email}`);
      setResetDialog(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handlePromoteUser = async (user) => {
    const isPromoting = user.role !== "admin";
    const action = isPromoting ? "promouvoir" : "rétrograder";
    if (!window.confirm(`Voulez-vous ${action} ${user.email} ${isPromoting ? "en administrateur" : "en utilisateur standard"} ?`)) return;
    try {
      if (isPromoting) {
        await api.promoteToAdmin({
          target_email: user.email,
          confirm_phrase: "PROMOTE TO ADMIN",
        });
        toast.success(`${user.email} est maintenant administrateur`);
      } else {
        await api.demoteFromAdmin({
          target_email: user.email,
          confirm_phrase: "DEMOTE FROM ADMIN",
        });
        toast.success(`${user.email} est maintenant utilisateur standard`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'opération");
    }
  };

  const genPassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789abcdefghjkmnpqrstuvwxyz";
    let p = "";
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setNewPassword(p);
  };

  const handleCreateTarget = async (e) => {
    e.preventDefault();
    try {
      await api.createMonthlyTarget({
        year: parseInt(targetData.year),
        month: parseInt(targetData.month),
        target_ca: parseFloat(targetData.target_ca)
      });
      toast.success("Objectif enregistré");
      setTargetDialogOpen(false);
      setTargetData({ year: currentYear, month: new Date().getMonth() + 1, target_ca: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Accès restreint</h2>
            <p className="text-muted-foreground">
              Cette page est réservée aux administrateurs.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="admin-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold">Administration</h1>
        <p className="text-muted-foreground mt-1">
          Gérez les utilisateurs et les paramètres
        </p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users" data-testid="admin-tab-users">
            <Users className="h-4 w-4 mr-2" />
            Utilisateurs
          </TabsTrigger>
          <TabsTrigger value="targets" data-testid="admin-tab-targets">
            <Target className="h-4 w-4 mr-2" />
            Objectifs CA
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={userFilter === "all" ? "default" : "outline"}
                onClick={() => setUserFilter("all")}
                data-testid="filter-users-all"
              >
                Tous ({users.length})
              </Button>
              <Button
                size="sm"
                variant={userFilter === "admin" ? "default" : "outline"}
                onClick={() => setUserFilter("admin")}
                data-testid="filter-users-admin"
              >
                Administrateurs ({users.filter(u => u.role === "admin").length})
              </Button>
              <Button
                size="sm"
                variant={userFilter === "user" ? "default" : "outline"}
                onClick={() => setUserFilter("user")}
                data-testid="filter-users-standard"
              >
                Utilisateurs ({users.filter(u => u.role !== "admin").length})
              </Button>
            </div>
            <Button onClick={() => setUserDialogOpen(true)} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Email</th>
                        <th>Rôle</th>
                        <th>Créé le</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.filter(u => userFilter === "all" || (userFilter === "admin" ? u.role === "admin" : u.role !== "admin")).map((user) => (
                        <tr key={user.id} data-testid={`user-row-${user.id}`}>
                          <td className="font-medium">{user.name}</td>
                          <td>
                            {user.email}
                            {locked[user.id] && <LockedBadge lock={locked[user.id]} userId={user.id} />}
                          </td>
                          <td>
                            <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                              {user.role === "admin" ? "Administrateur" : "Utilisateur"}
                            </Badge>
                          </td>
                          <td>{formatDate(user.created_at)}</td>
                          <td className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePromoteUser(user)}
                              data-testid={`toggle-admin-${user.id}`}
                              title={user.role === "admin" ? "Rétrograder en utilisateur" : "Promouvoir en administrateur"}
                            >
                              {user.role === "admin"
                                ? <ShieldOff className="h-4 w-4 text-slate-600" />
                                : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setResetDialog({ id: user.id, email: user.email }); setNewPassword(""); }}
                              data-testid={`reset-pwd-${user.id}`}
                              title="Réinitialiser le mot de passe"
                            >
                              <Key className="h-4 w-4 text-amber-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(user.id)}
                              data-testid={`delete-user-${user.id}`}
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                            {locked[user.id] && (
                              <UnlockButton userId={user.id} email={user.email} onDone={refreshLocked} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Targets Tab */}
        <TabsContent value="targets" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setTargetDialogOpen(true)} data-testid="add-target-btn">
              <Plus className="h-4 w-4 mr-2" />
              Définir un objectif
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : targets.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun objectif défini</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Période</th>
                        <th>Objectif CA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targets.map((target) => (
                        <tr key={target.id} data-testid={`target-row-${target.id}`}>
                          <td className="font-medium">
                            {monthNames[target.month - 1]} {target.year}
                          </td>
                          <td className="currency font-medium text-primary">
                            {formatCurrency(target.target_ca)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvel utilisateur</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user_name">Nom</Label>
              <Input
                id="user_name"
                value={userData.name}
                onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                required
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user_email">Email</Label>
              <Input
                id="user_email"
                type="email"
                value={userData.email}
                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                required
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user_password">Mot de passe</Label>
              <Input
                id="user_password"
                type="password"
                value={userData.password}
                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                required
                data-testid="user-password-input"
              />
            </div>
            <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2">
              Tous les comptes créés sont des comptes <span className="font-medium text-foreground">Utilisateur</span> (application isolée, aucune donnée partagée avec les autres comptes).
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="user-submit-btn">
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Target Dialog */}
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Définir un objectif CA</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTarget} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Année</Label>
                <Select
                  value={String(targetData.year)}
                  onValueChange={(v) => setTargetData({ ...targetData, year: v })}
                >
                  <SelectTrigger data-testid="target-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mois</Label>
                <Select
                  value={String(targetData.month)}
                  onValueChange={(v) => setTargetData({ ...targetData, month: v })}
                >
                  <SelectTrigger data-testid="target-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthNames.map((name, idx) => (
                      <SelectItem key={idx} value={String(idx + 1)}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target_ca">Objectif CA (€)</Label>
              <Input
                id="target_ca"
                type="number"
                step="0.01"
                min="0"
                value={targetData.target_ca}
                onChange={(e) => setTargetData({ ...targetData, target_ca: e.target.value })}
                placeholder="Ex: 150000"
                required
                data-testid="target-ca-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTargetDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="target-submit-btn">
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetDialog} onOpenChange={(o) => { if (!o) { setResetDialog(null); setNewPassword(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Compte : <span className="font-medium text-foreground">{resetDialog?.email}</span>
            </p>
            <div>
              <Label>Nouveau mot de passe (6 caractères min.)</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="ex: Sitex2026!"
                  data-testid="reset-pwd-input"
                  autoFocus
                />
                <Button type="button" variant="outline" onClick={genPassword} data-testid="reset-pwd-gen">
                  Générer
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ⚠️ Communiquez ce mot de passe à l'utilisateur par un canal sécurisé. Il n'est pas stocké en clair après cet enregistrement.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetDialog(null); setNewPassword(""); }}>Annuler</Button>
            <Button onClick={handleResetPassword} data-testid="reset-pwd-submit">Réinitialiser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
